import { UcoastEl } from '@/scripts/core/UcoastEl'
import { FEATURED_PRODUCT_VARIANT_CHANGE_EVENT } from '@/scripts/theme/constants'
import { type ProductForm } from '@/scripts/product/product-form'
import type { ProductVariant } from '@/scripts/shopify'
import {
	beginStandardProductSelection,
	resolveStandardProductSelection,
} from '@/scripts/platform/standard-events'
import { replaceProductUrlParameters } from '@/scripts/product/product-url'
import { motionEnabled } from '@/scripts/core/motion'

export class HeroFeaturedProduct extends UcoastEl {
	static htmlSelector = 'hero-featured-product'

	private productForm: HTMLFormElement | null = null
	private ATCButton: HTMLButtonElement | null = null
	private variants: NodeListOf<HTMLElement>
	private variantPannel: HTMLElement | null
	private heroMedia: HTMLElement | null
	private featuredMedia: HTMLElement | null
	private defaultFeaturedMedia: Node[]
	private selectShadeButton: HTMLButtonElement | null = null
	private shadeLabel: HTMLElement | null = null
	private panelRequest?: AbortController
	private shadePromptObserver?: MutationObserver
	private mediaPaused = false
	private panelTimeout?: number
	private requiresShade: boolean
	private hasSelectedShade: boolean
	private variantData: ProductVariant[]
	private featuredMediaRequest = 0
	private featuredMediaAnimation?: Animation
	private variantChangeFromKeyboard = false
	private prefetchedMedia?: {
		variantId: string
		fragment: DocumentFragment
		ready: Promise<boolean>
	}

	constructor() {
		super()

		this.productForm = this.querySelector<HTMLFormElement>('form') ?? null
		this.selectShadeButton = this.querySelector<HTMLButtonElement>('.select-shade__button') ?? null
		this.shadeLabel = this.querySelector<HTMLElement>('[data-selected-shade-label]') ?? null
		this.requiresShade = this.hasAttribute('data-requires-shade')
		this.hasSelectedShade = this.hasAttribute('data-has-selected-shade')
		const variantData = this.querySelector<HTMLScriptElement>(
			'[data-uc-variant-data]'
		)?.textContent
		this.variantData = variantData ? JSON.parse(variantData) : []

		this.ATCButton =
			this.productForm?.querySelector<HTMLButtonElement>(
				'.product-form__submit'
			) ?? null

		this.variants = this.querySelectorAll<HTMLElement>(
			'.hero__featured-product__variant'
		)

		const product = this.closest<HTMLElement>('.product')

		this.variantPannel = product?.querySelector<HTMLElement>('.product-media__panels') ?? null
		this.heroMedia = product?.querySelector<HTMLElement>('[data-product-hero-media]') ?? null
		this.featuredMedia = this.querySelector<HTMLElement>('[data-featured-product-media]') ?? null
		this.defaultFeaturedMedia = Array.from(
			this.featuredMedia?.childNodes ?? [],
			(node) => node.cloneNode(true)
		)


		if (!this.productForm || !this.ATCButton) {
			return
		}

		if (this.variants.length > 0 && !this.hasSelectedShade) {
			this.ATCButton.disabled = true
		}

		if (this.requiresShade && !this.hasSelectedShade) {
			this.ATCButton.type = 'button'
			this.ATCButton.addEventListener('click', this.handleShadePrompt)
			this.syncShadeDependentControls()
			const productInfo = this.closest('product-info')
			this.syncShadePrompt()
			if (productInfo) {
				this.shadePromptObserver = new MutationObserver(this.syncShadePrompt)
				this.shadePromptObserver.observe(productInfo, {
					attributes: true,
					attributeFilter: ['class'],
				})
			}
		}

		this.setupVariantListeners()

		if (this.selectShadeButton) {
			if (!this.requiresShade) this.ATCButton.disabled = false
			this.selectShadeButton.addEventListener('click', () => {
				this.classList.remove('has-shade')
			})
		}
	}

	override disconnectedCallback(): void {
		this.shadePromptObserver?.disconnect()
		this.featuredMediaRequest++
		this.featuredMediaAnimation?.cancel()
		this.prefetchedMedia = undefined
		this.ATCButton?.removeEventListener('click', this.handleShadePrompt)
		this.panelRequest?.abort()
		window.clearTimeout(this.panelTimeout)
		super.disconnectedCallback()
	}

	private syncShadePrompt = (): void => {
		if (this.hasSelectedShade || !this.ATCButton) return
		const expanded =
			this.closest('product-info')?.classList.contains('expanded') ?? false
		this.ATCButton.disabled = expanded
		if (expanded) this.ATCButton.setAttribute('aria-disabled', 'true')
		else this.ATCButton.removeAttribute('aria-disabled')
	}

	private handleShadePrompt = (event: MouseEvent): void => {
		if (this.hasSelectedShade) return

		event.preventDefault()
		this.closest('product-info')?.dispatchEvent(
			new CustomEvent('product-info:expand')
		)
	}

	private setupVariantListeners(): void {
		this.variants.forEach((variant) => {
			const input = variant.querySelector<HTMLInputElement>('input')

			if (!input) {
				return
			}

			input.addEventListener('change', () => {
				if (input.checked) {
					this.selectVariant(input, this.variantChangeFromKeyboard)
					this.variantChangeFromKeyboard = false
				}
			})
			input.addEventListener('keydown', () => {
				this.variantChangeFromKeyboard = true
			})
			variant.addEventListener('pointerdown', () => {
				this.variantChangeFromKeyboard = false
			})
			if (this.hasAttribute('data-stage-featured-media')) {
				const prefetch = (): void => {
					if (!this.prefetchedMedia && !input.checked) {
						this.prefetchedMedia = this.prepareFeaturedMedia(input.value, 'low')
					}
				}
				variant.addEventListener('pointerenter', prefetch)
				input.addEventListener('focus', prefetch)
			}
		})
	}

	private selectVariant(input: HTMLInputElement, fromKeyboard = false): void {
		if (!this.productForm || !this.ATCButton) {
			return
		}
		const variantId = input.value

		const variantInput =
			this.productForm.querySelector<HTMLInputElement>(
				'[data-uc-product-variant-id]'
			)

		if (!variantInput) {
			return
		}

		const variant = this.variantData.find(
			(candidate) => `${candidate.id}` === variantId
		)
		const standardSelection = beginStandardProductSelection(
			this,
			variant?.options ?? []
		)

		variantInput.value = variantId

		this.hasSelectedShade = true
		this.setAttribute('data-has-selected-shade', '')
		this.syncShadeDependentControls()
		if (variant?.title && this.shadeLabel) {
			this.shadeLabel.textContent = variant.title
		}
		this.ATCButton.disabled = false
		this.ATCButton.type = 'submit'
		this.updateCommerceState(input)
		this.updateProductUrl(variantId)
		this.renderFeaturedMedia(variantId, !fromKeyboard)

		this.variants.forEach((variant) => {
			const input = variant.querySelector<HTMLInputElement>('input')

			if (input) {
				input.checked = input.value === variantId
				variant.classList.toggle(
					'is-selected',
					input.value === variantId
				)
			}
		})

		this.closest('section')
			?.querySelector<ProductForm>('sticky-atc product-form')
			?.syncStickyFormFields()

		this.closest('.featured-product-carousel')?.dispatchEvent(
			new CustomEvent(FEATURED_PRODUCT_VARIANT_CHANGE_EVENT, {
				detail: { variantId },
			})
		)

		void this.renderVariantPanel(variantId)
		resolveStandardProductSelection(standardSelection, variant)
	}

	private updateProductUrl(variantId: string): void {
		if (!this.dataset.productUrl || this.dataset.updateUrl === 'false') return
		replaceProductUrlParameters(
			{ variant: variantId },
			this.dataset.productUrl
		)
	}

	private renderFeaturedMedia(variantId: string, animate: boolean): void {
		if (!this.featuredMedia) return
		if (this.hasAttribute('data-stage-featured-media')) {
			void this.renderReadyFeaturedMedia(variantId, animate)
			return
		}
		const template = Array.from(
			this.querySelectorAll<HTMLTemplateElement>('[data-variant-media-template]')
		).find((candidate) => candidate.dataset.variantMediaTemplate === variantId)
		const media = template
			? Array.from(template.content.childNodes, (node) => node.cloneNode(true))
			: this.defaultFeaturedMedia.map((node) => node.cloneNode(true))

		this.featuredMedia.replaceChildren(...media)
	}

	private prepareFeaturedMedia(variantId: string, priority: 'high' | 'low') {
		const template = Array.from(
			this.querySelectorAll<HTMLTemplateElement>('[data-variant-media-template]')
		).find((candidate) => candidate.dataset.variantMediaTemplate === variantId)
		if (!template) return undefined

		const fragment = document.importNode(template.content, true)
		const images = Array.from(fragment.querySelectorAll('img'))
		const ready = Promise.all(images.map(async (image) => {
			image.loading = 'eager'
			image.fetchPriority = priority
			await image.decode()
			return image.naturalWidth > 0
		})).then((results) => results.length > 0 && results.every(Boolean), () => false)
		return { variantId, fragment, ready }
	}

	private async renderReadyFeaturedMedia(variantId: string, animate: boolean): Promise<void> {
		const target = this.featuredMedia
		if (!target) return
		const request = ++this.featuredMediaRequest
		this.featuredMediaAnimation?.cancel()
		this.featuredMediaAnimation = undefined
		const staged = this.prefetchedMedia?.variantId === variantId
			? this.prefetchedMedia
			: this.prepareFeaturedMedia(variantId, 'high')
		this.prefetchedMedia = undefined
		staged?.fragment.querySelectorAll('img').forEach((image) => {
			image.fetchPriority = 'high'
		})
		if (!staged || !await staged.ready) return
		if (request !== this.featuredMediaRequest || !this.isConnected) return
		if (animate && motionEnabled() && target.childElementCount > 0) {
			const opacity = getComputedStyle(target).opacity
			this.featuredMediaAnimation = target.animate(
				[{ opacity }, { opacity: 0 }],
				{ duration: 120, easing: 'cubic-bezier(0.19, 1, 0.22, 1)', fill: 'forwards' }
			)
			await this.featuredMediaAnimation.finished.catch(() => undefined)
		}
		if (request !== this.featuredMediaRequest || !this.isConnected) return
		target.replaceChildren(staged.fragment)
		this.featuredMediaAnimation?.cancel()
		this.featuredMediaAnimation = animate && motionEnabled()
			? target.animate(
				[{ opacity: 0 }, { opacity: 1 }],
				{ duration: 180, easing: 'cubic-bezier(0.19, 1, 0.22, 1)' }
			)
			: undefined
	}

	private updateCommerceState(input: HTMLInputElement): void {
		if (!this.ATCButton) return
		const inventoryStatus = input.dataset.inventoryStatus
		const waitlist =
			inventoryStatus === 'ComingSoon' || inventoryStatus === 'OutOfStock'
		const available = input.dataset.variantAvailable === 'true'
		const inventoryStatusText = this.ATCButton.querySelector<HTMLElement>(
			'[data-uc-inventory-status-text]'
		)

		this.ATCButton.disabled = !available && !waitlist
		this.ATCButton.toggleAttribute('aria-disabled', !available && !waitlist)
		if (waitlist) {
			this.ATCButton.dataset.oosPopupTrigger = input.value
			this.ATCButton.dataset.productTitle = this.dataset.productTitle ?? ''
		} else {
			delete this.ATCButton.dataset.oosPopupTrigger
			delete this.ATCButton.dataset.productTitle
		}
		if (inventoryStatusText && input.dataset.inventoryActionText) {
			inventoryStatusText.textContent = input.dataset.inventoryActionText
		}
	}

	private clearSelectedMedia(variantId: string): void {
		const input = Array.from(this.variants)
			.flatMap((el) => Array.from(el.querySelectorAll('input')))
			.find((el) => el.value === variantId)
		const product = this.closest('.product')
		const copy = product?.querySelector<HTMLTemplateElement>(
			`template[data-selected-media-copy="${variantId}"]`
		)
		const regions = [this.heroMedia, this.variantPannel]
		const videos = regions.flatMap((region) =>
			Array.from(
				region?.querySelectorAll<
					HTMLElement & {
						userPaused?: boolean
						userPlayRequested?: boolean
					}
				>('ucoast-video') ?? []
			)
		)
		if (videos.some((video) => video.userPaused)) this.mediaPaused = true
		else if (videos.some((video) => video.userPlayRequested))
			this.mediaPaused = false
		for (const [index, region] of regions.entries()) {
			if (!region) continue
			const height = region.getBoundingClientRect().height
			if (height) region.style.minHeight = `${height}px`
			region.style.backgroundColor = input?.dataset.shadeColor ?? ''
			region.dataset.mediaState = 'loading'
			region.dataset.selectedMediaVariant = variantId
			region.toggleAttribute('data-media-paused', this.mediaPaused)
			const nextCopy = copy?.content.querySelector(
				`[data-media-copy-region="${index}"]`
			)
			if (index === 0) {
				const nextHeroPosition = nextCopy?.querySelector<HTMLTemplateElement>(
					'template[data-pdp-hero-copy-position]'
				)?.dataset.pdpHeroCopyPosition
				if (nextHeroPosition)
					region.dataset.pdpHeroContentPosition = nextHeroPosition
			}
			region.replaceChildren(
				...Array.from(nextCopy?.childNodes ?? [], (node) =>
					node.cloneNode(true)
				)
			)
			region
				.querySelector('template[data-pdp-hero-copy-position]')
				?.remove()
			this.bindLoadingToggle(region)
		}
	}

	private bindLoadingToggle(region: HTMLElement): void {
		const button = region.querySelector<HTMLButtonElement>(
			'[data-pdp-media-loading-control] [data-uc-video-toggle]'
		)
		if (!button) return
		this.updateLoadingToggle(button)
		button.addEventListener('click', this.handleLoadingToggle)
	}

	private handleLoadingToggle = (event: Event): void => {
		event.preventDefault()
		this.mediaPaused = !this.mediaPaused
		for (const region of [this.heroMedia, this.variantPannel]) {
			if (!region) continue
			region.toggleAttribute('data-media-paused', this.mediaPaused)
			region
				.querySelectorAll<HTMLButtonElement>(
					'[data-pdp-media-loading-control] [data-uc-video-toggle]'
				)
				.forEach((button) => this.updateLoadingToggle(button))
		}
	}

	private updateLoadingToggle(button: HTMLButtonElement): void {
		const state = this.mediaPaused ? 'paused' : 'playing'
		const label = this.mediaPaused
			? button.dataset.labelPlay
			: button.dataset.labelPause
		button.dataset.state = state
		if (label) {
			button.setAttribute('aria-label', label)
			const labelElement = button.querySelector<HTMLElement>(
				'[data-uc-video-toggle-label]'
			)
			if (labelElement) labelElement.textContent = label
		}
	}

	private async renderVariantPanel(variantId: string): Promise<void> {
		this.variantPannel ??=
			this.closest('.product')?.querySelector('.product-media__panels') ??
			null
		const sectionId = this.closest<HTMLElement>('section')?.dataset.section
		if (!sectionId || !this.heroMedia || !this.variantPannel) return
		this.panelRequest?.abort()
		window.clearTimeout(this.panelTimeout)
		const panelRequest = new AbortController()
		this.panelRequest = panelRequest
		this.clearSelectedMedia(variantId)
		this.panelTimeout = window.setTimeout(() => panelRequest.abort(), 8000)
		try {
			const url = new URL(window.location.href)
			url.searchParams.set('section_id', sectionId)
			url.searchParams.set('variant', variantId)
			const response = await fetch(url, { signal: panelRequest.signal })
			if (!response.ok)
				throw new Error(`Section render failed: ${response.status}`)
			const doc = new DOMParser().parseFromString(
				await response.text(),
				'text/html'
			)
			if (
				this.panelRequest !== panelRequest ||
				panelRequest.signal.aborted ||
				!this.isConnected
			)
				return
			const newHeroMedia = doc.querySelector<HTMLElement>(
				'[data-product-hero-media]'
			)
			const newVariantPanel = doc.querySelector<HTMLElement>('.product-media__panels')
			if (!newHeroMedia || !newVariantPanel)
				throw new Error('Section response is missing owned media')
			for (const region of [newHeroMedia, newVariantPanel]) {
				region.dataset.selectedMediaVariant = variantId
				region.dataset.mediaState = 'selected'
				region.style.backgroundColor =
					this.heroMedia.style.backgroundColor
				region.toggleAttribute('data-media-paused', this.mediaPaused)
			}
			this.heroMedia.replaceWith(newHeroMedia)
			this.heroMedia = newHeroMedia
			this.variantPannel.replaceWith(newVariantPanel)
			this.variantPannel = newVariantPanel
			const subscriptionSource = doc.getElementById(
				`Subscription-${sectionId}`
			)
			const subscriptionDestination = document.getElementById(
				`Subscription-${sectionId}`
			)
			if (subscriptionSource && subscriptionDestination) {
				subscriptionDestination.innerHTML = subscriptionSource.innerHTML
				subscriptionDestination.toggleAttribute(
					'hidden',
					subscriptionSource.hasAttribute('hidden')
				)
				this.syncShadeDependentControls()
			}
		} catch {
			if (this.panelRequest !== panelRequest || !this.isConnected) return
			for (const region of [this.heroMedia, this.variantPannel])
				if (region) region.dataset.mediaState = 'failed'
			const retry = document.createElement('button')
			retry.type = 'button'
			retry.className = 'button'
			retry.textContent = this.dataset.mediaRetryLabel ?? ''
			retry.addEventListener(
				'click',
				() => {
					this.querySelector<HTMLInputElement>(
						'input:checked'
					)?.focus({ preventScroll: true })
					void this.renderVariantPanel(variantId)
				},
				{ once: true }
			)
			this.heroMedia?.append(retry)
		} finally {
			if (this.panelRequest === panelRequest) {
				window.clearTimeout(this.panelTimeout)
				this.panelRequest = undefined
			}
		}
	}

	private syncShadeDependentControls(): void {
		const inactive = this.requiresShade && !this.hasSelectedShade
		if (inactive) this.shadeLabel?.setAttribute('aria-disabled', 'true')
		else this.shadeLabel?.removeAttribute('aria-disabled')
		const subscription = this.querySelector<HTMLElement>(
			'[data-requires-product-selection]'
		)
		if (!subscription) return

		subscription.toggleAttribute('data-inactive', inactive)
		if (inactive) subscription.setAttribute('aria-disabled', 'true')
		else subscription.removeAttribute('aria-disabled')
		subscription
			.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
				'input:not([type="hidden"]), button'
			)
			.forEach((control) => {
				control.disabled = inactive
			})
	}
}
